import React, { useState, useEffect, useMemo } from "react";
import { FaTimes } from "react-icons/fa"; // Para o botão de fechar do modal
import {
  WiDaySunny,
  WiDayCloudy,
  WiCloud,
  WiCloudy,
  WiRain, // Usaremos este para chuva genérica
  WiThunderstorm, // Usaremos este para trovoada
  WiSnow,
  WiFog,
} from "react-icons/wi";

const VITORIA = { lat: -20.3155, lon: -40.3128 };
const TZ = "America/Sao_Paulo";

// Códigos de tempo que indicam CHUVA/GAROA (excluindo Trovoada)
const GENERAL_RAIN_CODES = [
  51,
  53,
  55, // Chuvisco
  56,
  57, // Chuvisco Congelante
  61,
  63,
  65, // Chuva
  66,
  67, // Chuva Congelante
  80,
  81,
  82, // Pancadas de Chuva
];

// Códigos de Trovoada (máxima prioridade visual)
const THUNDERSTORM_CODES = [95, 96, 99]; // Trovoada

// Agrupa todos os códigos que indicam risco de precipitação
const ALL_PRECIPITATION_CODES = [...GENERAL_RAIN_CODES, ...THUNDERSTORM_CODES];

function mapWeatherCodeToIcon(code: number) {
  // 1. Trovoada (Prioridade máxima)
  if (THUNDERSTORM_CODES.includes(code))
    return <WiThunderstorm className="text-xl sm:text-2xl text-blue-400" />;

  // 2. Chuva (Geral)
  if (GENERAL_RAIN_CODES.includes(code))
    return <WiRain className="text-xl sm:text-2xl text-blue-400" />;

  // 3. Mapeamento original para outros códigos
  if ([0].includes(code)) return <WiDaySunny className="text-xl sm:text-2xl" />;
  if ([1, 2].includes(code))
    return <WiDayCloudy className="text-xl sm:text-2xl" />;
  if ([3].includes(code)) return <WiCloudy className="text-xl sm:text-2xl" />;
  if ([45, 48].includes(code)) return <WiFog className="text-xl sm:text-2xl" />;
  if ([71, 73, 75, 77, 85, 86].includes(code))
    return <WiSnow className="text-xl sm:text-2xl" />;

  return <WiCloud className="text-xl sm:text-2xl" />;
}

function isoDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextTuesday(now = new Date()) {
  const d = new Date(now);
  const w = d.getDay(); // 0..6 (2=terça)
  const add = (2 - w + 7) % 7;
  // Se for terça-feira hoje e a hora já passou das 21h, pula para a próxima terça.
  if (add === 0 && d.getHours() > 21) {
    d.setDate(d.getDate() + 7);
  } else if (add !== 0) {
    d.setDate(d.getDate() + add);
  }

  d.setHours(0, 0, 0, 0);
  return d;
}

// NOVO TIPO DE DADO para a previsão horária detalhada
type DetailHour = {
  hour: string; // Ex: "19h"
  temp: number;
  code: number;
};

// Tipo de dado principal modificado para incluir o detalhe
type WeatherData = {
  temp: number;
  code: number;
  hourISO: string;
  rainDetected: boolean;
  detailedForecast: DetailHour[]; // NOVO: Detalhes das 19h, 20h, 21h
};

// --- Componente do Modal ---
interface WeatherDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: string;
  forecasts: DetailHour[];
}

const WeatherDetailModal: React.FC<WeatherDetailModalProps> = ({
  isOpen,
  onClose,
  day,
  forecasts,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 text-white rounded-lg shadow-2xl w-full max-w-sm border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold">Previsão Detalhada {day}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {forecasts.map((f, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg ${
                ALL_PRECIPITATION_CODES.includes(f.code)
                  ? "bg-pink-800/50"
                  : "bg-gray-700/50"
              }`}
            >
              <span className="font-semibold text-base w-10">{f.hour}</span>
              <div className="flex items-center gap-3">
                {mapWeatherCodeToIcon(f.code)}
                <span className="font-bold text-lg w-12 text-right">
                  {f.temp}°C
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 text-xs text-gray-400 border-t border-gray-700">
          A cor de fundo rosa indica **risco de chuva/trovoada** no horário.
        </div>
      </div>
    </div>
  );
};
// --- Fim do Componente do Modal ---

const WeatherBadge: React.FC = () => {
  const [loading, setLoading] = useState(true);
  // Renomeado 'hour' para 'mainForecast' para maior clareza
  const [mainForecast, setMainForecast] = useState<WeatherData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // NOVO: Estado do modal

  const targetDate = useMemo(() => nextTuesday(new Date()), []);
  const isTodayTuesday = useMemo(() => {
    const n = new Date();
    n.setHours(0, 0, 0, 0);
    return n.getTime() === targetDate.getTime();
  }, [targetDate]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const dayStr = isoDate(targetDate);
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${
          VITORIA.lat
        }&longitude=${
          VITORIA.lon
        }&hourly=temperature_2m,weathercode&timezone=${encodeURIComponent(
          TZ
        )}&start_date=${dayStr}&end_date=${dayStr}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Falha ao buscar previsão do tempo");

        const json = await res.json();
        const times: string[] = json?.hourly?.time || [];
        const temps: number[] = json?.hourly?.temperature_2m || [];
        const codes: number[] = json?.hourly?.weathercode || [];

        let rainFound = false;
        const targetHours = ["T19:00", "T20:00", "T21:00"];
        const detailedForecast: DetailHour[] = [];

        // 1. Coletar dados detalhados e verificar chuva
        for (let i = 0; i < times.length; i++) {
          const timeStr = times[i];
          const code = codes[i];
          const hourPart = timeStr.substring(timeStr.length - 6);

          if (targetHours.includes(hourPart)) {
            const hourNumber = parseInt(hourPart.substring(1, 3));
            detailedForecast.push({
              hour: `${hourNumber}h`,
              temp: Math.round(temps[i]),
              code: codes[i],
            });

            if (ALL_PRECIPITATION_CODES.includes(code)) {
              rainFound = true;
            }
          }
        }

        // Ordenar os detalhes para garantir 19h, 20h, 21h
        detailedForecast.sort((a, b) => parseInt(a.hour) - parseInt(b.hour));

        // 2. Encontrar o dado das 19:00 (ou mais próximo) para a temperatura principal
        let idx = times.findIndex((t) => t.endsWith("T19:00"));

        if (idx === -1) {
          const target = new Date(`${dayStr}T19:00:00`);
          let best = 0,
            bestDiff = Infinity;
          times.forEach((t, i) => {
            const diff = Math.abs(new Date(t).getTime() - target.getTime());
            if (diff < bestDiff) {
              bestDiff = diff;
              best = i;
            }
          });
          idx = best;
        }

        setMainForecast({
          temp: Math.round(temps[idx]),
          code: codes[idx],
          hourISO: times[idx],
          rainDetected: rainFound,
          detailedForecast: detailedForecast,
        });
      } catch (e) {
        console.error("Erro ao obter previsão do tempo:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [targetDate]);

  if (loading || !mainForecast) {
    return (
      <div className="flex items-center gap-2 bg-gray-800/70 border border-gray-700 rounded px-2 py-1">
        <div className="w-4 h-4 rounded-full bg-pink-500 animate-pulse" />
        <span className="text-xs text-gray-300">Carregando…</span>
      </div>
    );
  }

  // Lógica de Prioridade: Se chuva detectada E não for Trovoada, força o código para Chuva Leve (61)
  const finalCode =
    mainForecast.rainDetected && !THUNDERSTORM_CODES.includes(mainForecast.code)
      ? 61
      : mainForecast.code;

  const icon = mapWeatherCodeToIcon(finalCode);

  const dayLabel = isTodayTuesday ? "terça" : "próx. terça";
  const modalDayLabel = isTodayTuesday
    ? "De Hoje"
    : `Da Próxima Terça-feira (${targetDate.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
      })})`;

  return (
    <>
      <div
        className="flex items-center gap-2 bg-gray-800/70 border border-gray-700 rounded px-2 py-1 cursor-pointer hover:bg-gray-700 transition-colors"
        onClick={() => setIsModalOpen(true)} // AÇÃO DE CLIQUE AQUI
        title="Clique para ver detalhes da previsão 19h-21h"
      >
        {icon}
        <span className="text-sm font-semibold">{mainForecast.temp}°</span>
        <span className="text-[11px] text-gray-300">{dayLabel}</span>
      </div>

      {/* NOVO: Componente Modal */}
      <WeatherDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        day={modalDayLabel}
        forecasts={mainForecast.detailedForecast}
      />
    </>
  );
};

export default WeatherBadge;
